import React, { createContext, useContext, useEffect, useRef, useState } from "react";

const ThemeCtx = createContext({ theme: "dark", toggle: () => {}, setTheme: () => {} });

const STORAGE_KEY = "bliencer_theme";

function initialTheme() {
  if (typeof window === "undefined") return "dark";
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === "dark" || saved === "light") return saved;
  return "dark";
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(initialTheme);
  const flipTimer = useRef(null);
  const firstRun = useRef(true);

  useEffect(() => {
    const html = document.documentElement;
    // Suppress transitions across the whole app during the theme flip so
    // hundreds of elements don't all start animating at once (was causing
    // visible jank when switching modes). We paint the new theme in the
    // same frame, then re-enable transitions ~200 ms later.
    if (!firstRun.current) {
      html.classList.add("theme-changing");
      if (flipTimer.current) clearTimeout(flipTimer.current);
      flipTimer.current = setTimeout(() => {
        html.classList.remove("theme-changing");
      }, 200);
    }
    firstRun.current = false;

    html.classList.remove("light", "dark");
    html.classList.add(theme);
    localStorage.setItem(STORAGE_KEY, theme);
    // update meta theme-color for mobile browser chrome
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme === "dark" ? "#000000" : "#ffffff");
  }, [theme]);

  useEffect(() => () => {
    if (flipTimer.current) clearTimeout(flipTimer.current);
  }, []);

  const toggle = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return (
    <ThemeCtx.Provider value={{ theme, toggle, setTheme }}>{children}</ThemeCtx.Provider>
  );
}

export const useTheme = () => useContext(ThemeCtx);
